import React, { useState, useEffect } from 'react';
import {
    CFormGroup,
    CLabel,
    CInput,
    CButton,
    CRow,
    CCol,
    CSpinner,
} from '@coreui/react';
import Select from 'react-select';
import { getApi } from '../../apis/backendApis';
import Form, { FormApi } from '../../components/form';
import useCustomForm from '../../services/useCustomForm';
import useCurrentAction from '../../services/useCurrentAction';

const WebhookMattermostForm = () => {
    const [formApi, setFormApi] = useState<FormApi>();
    const action = useCurrentAction();
    const isView = () => action === 'view';
    const isEdit = () => action === 'edit';

    const [canais, setCanais] = useState<any[]>([]);
    const [selectedCanal, setSelectedCanal] = useState<any>(null);
    const [syncingCanais, setSyncingCanais] = useState(false);

    const initialValues = {
        canal: null,
        username: '',
        idWebhook: '',
        iconUrl: '',
    };

    const {
        values,
        setValues,
        handleChange,
        handleSubmit,
        handleSubmitValues,
    } = useCustomForm({
        initialValues,
        route: 'mattermost/webhook',
        onSubmit: (data: any) => {
            const submitData = { ...data.values };
            submitData.canal = selectedCanal?.value ?? null;
            handleSubmitValues(submitData);
        },
        onLoadDadosCompleted: (data: any) => {
            setValues(data);
            if (data.canal) {
                setSelectedCanal({ value: data.canal, label: '' });
            }
        },
    });

    const loadCanais = () => {
        getApi()
            .get('/mattermost/canal/list')
            .then((response: any) => {
                const rows: any[] = response.data?.rows ?? [];
                const options = rows.map((c: any) => ({ value: c.id, label: c.nome }));
                setCanais(options);

                if (values.canal) {
                    const match = options.find((o: any) => o.value === values.canal);
                    if (match) setSelectedCanal(match);
                }
            })
            .catch(() => {});
    };

    useEffect(() => {
        loadCanais();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (values.canal && canais.length > 0) {
            const match = canais.find((o: any) => o.value === values.canal);
            if (match) setSelectedCanal(match);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values.canal, canais]);

    const handleSyncCanais = () => {
        setSyncingCanais(true);
        getApi()
            .get('/mattermost/channels')
            .then(() => loadCanais())
            .catch(() => {})
            .finally(() => setSyncingCanais(false));
    };

    const handleFormReady = (api: FormApi) => setFormApi(api);

    return (
        <div className="w-100">
            <Form entity="WebhookMattermost" handleSubmit={handleSubmit} onFormReady={handleFormReady}>

                <CFormGroup>
                    <CLabel className="text-danger" htmlFor="canal">Canal</CLabel>
                    <CRow className="align-items-center">
                        <CCol sm="10">
                            {!isView() ? (
                                <Select
                                    inputId="canal"
                                    options={canais}
                                    value={selectedCanal}
                                    readOnly={isEdit()}
                                    onChange={(opt: any) => setSelectedCanal(opt)}
                                    placeholder="Selecione um canal..."
                                    isDisabled={isEdit() ||isView()}
                                />
                            ) : (
                                <CInput
                                    readOnly
                                    disabled
                                    value={selectedCanal?.label || ''}
                                />
                            )}
                        </CCol>
                        {!isView() && (
                            <CCol sm="2">
                                <CButton
                                    color="secondary"
                                    size="sm"
                                    className="w-100"
                                    onClick={handleSyncCanais}
                                    disabled={syncingCanais}
                                >
                                    {syncingCanais
                                        ? <CSpinner size="sm" />
                                        : 'Atualizar Lista de Canais'}
                                </CButton>
                            </CCol>
                        )}
                    </CRow>
                </CFormGroup>

                <CFormGroup>
                    <CLabel className="text-danger" htmlFor="username">Nome do Bot (exibido no canal)</CLabel>
                    <CInput
                        readOnly={isView()}
                        disabled={isView()}
                        type="text"
                        id="username"
                        name="username"
                        onChange={handleChange}
                        value={values.username}
                        required
                    />
                    {!isView() && (
                        <small className="text-muted">
                            Será convertido automaticamente para formato válido (minúsculas, espaços → hífens). Ex: "Meu Bot" → "meu-bot"
                        </small>
                    )}
                </CFormGroup>

                <CFormGroup>
                    <CLabel htmlFor="iconUrl">URL do Ícone do Bot (opcional)</CLabel>
                    <CInput
                        readOnly={isView()}
                        disabled={isView()}
                        type="url"
                        id="iconUrl"
                        name="iconUrl"
                        onChange={handleChange}
                        value={values.iconUrl || ''}
                        placeholder="https://exemplo.com/icone.png"
                    />
                </CFormGroup>

                {isView() && values.idWebhook && (
                    <CFormGroup>
                        <CLabel htmlFor="idWebhook">ID do Webhook (Mattermost)</CLabel>
                        <CInput
                            readOnly
                            disabled
                            id="idWebhook"
                            value={values.idWebhook}
                        />
                    </CFormGroup>
                )}

            </Form>
        </div>
    );
};

export default WebhookMattermostForm;
